import { IEarnedEvent, QuotaInDaysAndHours, QuotaInUnits } from '@vacationtracker/shared/types/calculations'
import {
  differenceInBusinessDays,
  getDaysInMonth,
  addDays,
  getDay,
  nextDay,
  subDays,
  differenceInDays,
  isAfter,
  eachWeekOfInterval,
  addWeeks,
  setDay,
  isBefore,
  endOfDay,
  isLastDayOfMonth,
  lastDayOfMonth,
  min,
  endOfMonth,
} from 'date-fns'
import { IQuotaAmount } from '../types/leave-policy'

/**
 * Round number to 4 decimals
 * Note:
 * Number.EPSILON is the difference between 1 and the next number existing in the double precision floating point numbers.
 * It is relevant only when the value to round is the result of an arithmetic operation, as it can swallow some floating point error delta.
 * https://stackoverflow.com/a/48764436
 */
export function roundTo4Decimals(num: number): number {
  return Math.round((num + Number.EPSILON) * 10000) / 10000
}

export function roundTo2Decimals(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100
}

/**
 * Sum days from all earned events
 */
export function sumEarnedEvents(earnedEvents: Omit<IEarnedEvent, 'amount'>[], averageWorkingHours: number): QuotaInDaysAndHours {
  const result = earnedEvents.reduce((acc, earnedEvent) => {
    return acc + earnedEvent.amountInUnits.value
  }, 0)
  const lastIndex = earnedEvents.length - 1
  const unit = lastIndex === -1 ? 'days' : earnedEvents[earnedEvents.length - 1].amountInUnits.unit
  return {
    unit,
    days: unit === 'days' ? roundTo4Decimals(result) : roundTo4Decimals(result / averageWorkingHours),
    hours: unit === 'hours' ? roundTo4Decimals(result) : roundTo4Decimals(result * averageWorkingHours),
  }
}

/**
 * Prorated amount of earned days in one period.
 * Can be userd for any period - year, month, half month, week, etc...
 * User can start or end employment at any point during the period, so we must prorate the earned amount.
 */
export function proRateQuotaByBusinessDays(periodFrom: Date, periodTo: Date, workedFromDate: Date, workedToDate: Date, fullPeriodDays: QuotaInUnits): QuotaInUnits {
  const workDaysInWholePeriod = differenceInBusinessDays(addDays(periodTo, 1), periodFrom)
  const daysWorkedByUserInPeriod = differenceInBusinessDays(addDays(workedToDate, 1), workedFromDate)

  return {
    unit: fullPeriodDays.unit,
    value: roundTo4Decimals(fullPeriodDays.value * (daysWorkedByUserInPeriod / workDaysInWholePeriod)),
  }
}

/**
 * Days earned for last period, to take care of the rounding error
 */
export function getEarningForLastPeriod(quotaForCurrentYear: QuotaInUnits, earnedEventsSoFar: Omit<IEarnedEvent, 'amount'>[], averageWorkingHours: number): QuotaInUnits {
  const earnedDaysSoFar = sumEarnedEvents(earnedEventsSoFar, averageWorkingHours)
  const unit = quotaForCurrentYear.unit
  return {
    unit,
    value: roundTo4Decimals(quotaForCurrentYear.value - earnedDaysSoFar[unit]),
  }
}

/**
 * Payout date for periods in Monthly calculation
 * @param periodEnd End of the monthly period (YYYY-MM-DD)
 * @param firstEarningDate Date chosen in the date picker as the first payout date after configuring accruals (YYYY-MM-DD)
 * @param accrualPeriodStart Optional accrual period start date (YYYY-MM-DD)
 * @returns Payout date as Date object
 */
export function getPayoutDateForMonthlyPeriod(periodEnd: string, firstEarningDate: string, accrualPeriodStart?: string): Date {
  // Convert strings to Date objects for internal calculations
  // Add T00:00:00 to parse as local time instead of UTC
  const periodEndDate = new Date(periodEnd + 'T00:00:00')
  const firstEarningDateObj = new Date(firstEarningDate + 'T00:00:00')
  const accrualPeriodStartDate = accrualPeriodStart ? new Date(accrualPeriodStart + 'T00:00:00') : undefined

  const payoutDayOfMonth = firstEarningDateObj.getDate()

  const currentPeriodEndDayOfMonth = periodEndDate.getDate()
  const isPayoutOnLastDayOfMonth = payoutDayOfMonth === getDaysInMonth(firstEarningDateObj)

  if (accrualPeriodStartDate) {
    const firstEarningDateMonth = firstEarningDateObj.getMonth()
    const firstEarningDateDay = firstEarningDateObj.getDate()
    const accrualPeriodStartMonth = accrualPeriodStartDate.getMonth()
    const accrualPeriodStartDay = accrualPeriodStartDate.getDate()
    const isOneDayBeforeLastDayOfMonth =
      differenceInDays(lastDayOfMonth(accrualPeriodStartDate), new Date(accrualPeriodStartDate.getFullYear(), accrualPeriodStartDate.getMonth(), payoutDayOfMonth)) === 1
    const isTwoDaysBeforeLastDayOfMonth =
      differenceInDays(lastDayOfMonth(accrualPeriodStartDate), new Date(accrualPeriodStartDate.getFullYear(), accrualPeriodStartDate.getMonth(), payoutDayOfMonth)) === 2

    if (isLastDayOfMonth(firstEarningDateObj)) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), lastDayOfMonth(periodEndDate).getDate())
    } else if (isOneDayBeforeLastDayOfMonth) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), subDays(lastDayOfMonth(periodEndDate), 1).getDate())
    } else if (isTwoDaysBeforeLastDayOfMonth) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), subDays(lastDayOfMonth(periodEndDate), 2).getDate())
    } else if (firstEarningDateMonth === accrualPeriodStartMonth && payoutDayOfMonth === 1) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), payoutDayOfMonth)
    } else if (firstEarningDateMonth === accrualPeriodStartMonth && firstEarningDateDay > accrualPeriodStartDay && accrualPeriodStartDay !== 1) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() - 1, payoutDayOfMonth)
    } else if (firstEarningDateMonth === accrualPeriodStartMonth && accrualPeriodStartDay >= payoutDayOfMonth) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() - 1, payoutDayOfMonth)
    } else if (firstEarningDateMonth === accrualPeriodStartMonth + 1 && accrualPeriodStartDay > payoutDayOfMonth) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), payoutDayOfMonth)
    } else if (firstEarningDateMonth === accrualPeriodStartMonth) {
      return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), payoutDayOfMonth)
    }
  }

  if (isPayoutOnLastDayOfMonth) {
    const daysInMonth = getDaysInMonth(periodEndDate)
    return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), daysInMonth)
  } else if (currentPeriodEndDayOfMonth <= payoutDayOfMonth) {
    const daysInMonth = getDaysInMonth(periodEndDate)
    const adjustedPayoutDay = daysInMonth < payoutDayOfMonth ? daysInMonth : payoutDayOfMonth
    return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), adjustedPayoutDay)
  } else {
    const daysInMonth = getDaysInMonth(new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1))
    const adjustedPayoutDay = daysInMonth < payoutDayOfMonth ? daysInMonth : payoutDayOfMonth
    return new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, adjustedPayoutDay)
  }
}

/**
 * Payout date for periods in Semi-Monthly calculation
 * If we dont have accrualPeriodStart, firstEarningDate can be last day of the current period and any date in the next period.
 * If we have accrualPeriodStart, firstEarningDate can be any date in the current period or any date in the next period.
 * @param periodStart Start of the semi-monthly period (YYYY-MM-DD)
 * @param periodEnd End of the semi-monthly period (YYYY-MM-DD)
 * @param firstEarningDate Date chosen in the date picker as the first payout date after configuring accruals (YYYY-MM-DD)
 * @param accrualPeriodStart optional, only when first earning date is during the period or last day after period (YYYY-MM-DD)
 * @param firstPayoutDateOnOrAfterThisDate optional, pass today date (YYYY-MM-DD)
 * @returns Payout date as Date object
 */
export function getPayoutDateForSemiMonthlyPeriod({
  periodStart,
  periodEnd,
  firstEarningDate,
  accrualPeriodStart,
  firstPayoutDateOnOrAfterThisDate,
}: {
  periodStart: string
  periodEnd: string
  firstEarningDate: string
  accrualPeriodStart?: string
  firstPayoutDateOnOrAfterThisDate?: string
}): Date {
  // Convert strings to Date objects for internal calculations
  // Add T00:00:00 to parse as local time instead of UTC
  const periodStartDate = new Date(periodStart + 'T00:00:00')
  const periodEndDate = new Date(periodEnd + 'T00:00:00')
  const firstEarningDateObj = new Date(firstEarningDate + 'T00:00:00')
  const accrualPeriodStartDate = accrualPeriodStart ? new Date(accrualPeriodStart + 'T00:00:00') : undefined
  const firstPayoutDateOnOrAfterThisDateObj = firstPayoutDateOnOrAfterThisDate ? new Date(firstPayoutDateOnOrAfterThisDate + 'T00:00:00') : undefined

  // Calculate the two payout days in a month
  // Use accrualPeriodStart if provided AND it's not on standard days (1 or 16), otherwise use firstEarningDate
  const accrualStartDay = accrualPeriodStartDate?.getDate()
  const useAccrualStartForPayoutDays = accrualPeriodStartDate && accrualPeriodStart !== '1970-01-01' && accrualStartDay !== 1 && accrualStartDay !== 16

  const referenceDate = useAccrualStartForPayoutDays ? accrualPeriodStartDate : firstEarningDateObj
  const referenceDayOfMonth = referenceDate!.getDate()
  const daysInReferenceMonth = getDaysInMonth(referenceDate!)

  // Calculate the second payout day based on the reference date
  let secondPayoutDay: number
  if (referenceDayOfMonth === daysInReferenceMonth && referenceDayOfMonth > 15) {
    // Special case: if reference date is the last day of the month, the other payout day is 15
    secondPayoutDay = 15
  } else if (referenceDayOfMonth > 15) {
    // If reference date is in second half, subtract 15 days
    secondPayoutDay = subDays(referenceDate!, 15).getDate()
  } else {
    // If reference date is in first half, add 15 days (but cap at end of month)
    secondPayoutDay = min([addDays(referenceDate!, 15), endOfMonth(referenceDate!)]).getDate()
  }

  const [payoutDay1, payoutDay2] = [referenceDayOfMonth, secondPayoutDay].sort((a, b) => a - b)
  const firstPayoutDay = firstEarningDateObj.getDate()

  const periodStartDay = periodStartDate.getDate()
  const periodEndDay = periodEndDate.getDate()
  const daysInMonth = getDaysInMonth(periodEndDate)
  const isFirstHalfOfMonth = periodStartDay === 1

  // BRANCH 1: With accrualPeriodStart - determine pattern based on relationship between dates
  if (accrualPeriodStartDate && accrualPeriodStart !== '1970-01-01') {
    const accrualStartDay = accrualPeriodStartDate.getDate()
    const accrualPeriodIsFirstHalf = accrualStartDay <= 15

    // Check if this is the exact period containing accrualPeriodStart (the very first period)
    const isPeriodWithAccrualStart =
      periodStartDate.getMonth() === accrualPeriodStartDate.getMonth() &&
      periodStartDate.getFullYear() === accrualPeriodStartDate.getFullYear() &&
      periodStartDay === (accrualStartDay <= 15 ? 1 : 16)

    // Check if firstEarningDate is within or at the end of this period
    const firstEarningIsInThisPeriod =
      firstEarningDateObj.getMonth() === periodEndDate.getMonth() && firstEarningDateObj.getFullYear() === periodEndDate.getFullYear() && firstPayoutDay <= periodEndDate.getDate()

    let payoutDate: Date

    // For the first period, return the exact firstEarningDate only if it's in this period
    if (isPeriodWithAccrualStart && firstEarningIsInThisPeriod) {
      payoutDate = new Date(firstEarningDateObj.getFullYear(), firstEarningDateObj.getMonth(), firstPayoutDay)
    } else {
      // Determine if pre-paid (earning in same period) or post-paid (earning in next period)
      const isPrePaid = accrualPeriodIsFirstHalf ? firstPayoutDay <= 15 : firstPayoutDay > 15

      // Apply the pattern for all other periods
      if (isPrePaid) {
        // Pre-paid: payout at period start (1st or 16th)
        if (isFirstHalfOfMonth) {
          payoutDate = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), payoutDay1)
        } else {
          // For second half, use last day of month if payoutDay2 is at month end (28-31)
          const payoutDay = [28, 29, 30, 31].includes(payoutDay2) ? daysInMonth : payoutDay2
          payoutDate = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), payoutDay)
        }
      } else {
        // Post-paid: payout after period ends
        payoutDate = isFirstHalfOfMonth
          ? new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), payoutDay2)
          : new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, payoutDay1)
      }
    }

    // Check if the payout date needs to be adjusted to be on or after the minimum date
    if (firstPayoutDateOnOrAfterThisDateObj && isBefore(payoutDate, firstPayoutDateOnOrAfterThisDateObj)) {
      // Calculate next period
      const nextPeriodStart = isFirstHalfOfMonth
        ? new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), 16)
        : new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1)
      const nextPeriodEnd = isFirstHalfOfMonth
        ? new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), getDaysInMonth(periodEndDate))
        : new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 15)

      return getPayoutDateForSemiMonthlyPeriod({
        periodStart: nextPeriodStart.toISOString().split('T')[0],
        periodEnd: nextPeriodEnd.toISOString().split('T')[0],
        firstEarningDate,
        accrualPeriodStart,
        firstPayoutDateOnOrAfterThisDate,
      })
    }

    return payoutDate
  }

  // BRANCH 2: Without accrualPeriodStart - standard logic
  let payoutDate: Date

  if (periodEndDay <= payoutDay1) {
    payoutDate = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), payoutDay1)
  } else if (periodEndDay <= payoutDay2) {
    // Adjust payoutDay2 if it exceeds days in month (e.g., 30 in February)
    const adjustedDay = Math.min(payoutDay2, daysInMonth)
    payoutDate = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), adjustedDay)
  } else if (periodEndDay === daysInMonth && (payoutDay2 === daysInMonth || (payoutDay2 === 30 && daysInMonth === 31))) {
    // Period ends on last day of month and payoutDay2 is near end of month - payout on month end
    payoutDate = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), daysInMonth)
  } else {
    // Period ends after payout day 2 - move to next month
    payoutDate = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, payoutDay1)
  }

  // Check if the payout date needs to be adjusted to be on or after the minimum date
  if (firstPayoutDateOnOrAfterThisDateObj && isBefore(payoutDate, firstPayoutDateOnOrAfterThisDateObj)) {
    // Calculate next period
    const nextPeriodStart = isFirstHalfOfMonth
      ? new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), 16)
      : new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1)
    const nextPeriodEnd = isFirstHalfOfMonth
      ? new Date(periodEndDate.getFullYear(), periodEndDate.getMonth(), getDaysInMonth(periodEndDate))
      : new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 15)

    return getPayoutDateForSemiMonthlyPeriod({
      periodStart: nextPeriodStart.toISOString().split('T')[0],
      periodEnd: nextPeriodEnd.toISOString().split('T')[0],
      firstEarningDate,
      accrualPeriodStart,
      firstPayoutDateOnOrAfterThisDate,
    })
  }

  return payoutDate
}

/**
 * Payout date for periods in Bi-Weekly calculation
 * @param periodStart start of the bi-weekly period (YYYY-MM-DD)
 * @param periodEnd End of the bi-weekly period (YYYY-MM-DD)
 * @param firstEarningDate Date chosen in the date picker as the first payout date after configuring accruals (YYYY-MM-DD)
 * @param accrualPeriodStart Optional accrual period start date (YYYY-MM-DD)
 * @returns Payout date as Date object
 */
export function getPayoutDateForBiWeeklyPeriod(periodStart: string, periodEnd: string, firstEarningDate: string, accrualPeriodStart?: string): Date {
  // Add T00:00:00 to parse as local time instead of UTC
  const payoutDayOfWeek = getDay(new Date(firstEarningDate + 'T00:00:00'))

  if (
    accrualPeriodStart &&
    accrualPeriodStart !== '1970-01-01' &&
    !isBefore(new Date(firstEarningDate + 'T00:00:00'), new Date(accrualPeriodStart + 'T00:00:00')) &&
    isAfter(addDays(new Date(accrualPeriodStart + 'T00:00:00'), 13), new Date(firstEarningDate + 'T00:00:00'))
  ) {
    const differenceInWeeks = eachWeekOfInterval({
      start: new Date(accrualPeriodStart + 'T00:00:00'),
      end: new Date(firstEarningDate + 'T00:00:00'),
    }).length

    return endOfDay(setDay(addWeeks(new Date(periodStart + 'T00:00:00'), differenceInWeeks - 1), payoutDayOfWeek))
  }

  return endOfDay(nextDay(subDays(new Date(periodEnd + 'T00:00:00'), 1), payoutDayOfWeek))
}

/**
 * Payout date for periods in Weekly calculation
 * @param periodStart start of the weekly period (YYYY-MM-DD)
 * @param periodEnd End of the weekly period (YYYY-MM-DD)
 * @param firstEarningDate Date chosen in the date picker as the first payout date after configuring accruals (YYYY-MM-DD)
 * @param accrualPeriodStart Optional accrual period start date (YYYY-MM-DD)
 * @returns Payout date as Date object
 */
export function getPayoutDateForWeeklyPeriod(periodStart: string, periodEnd: string, firstEarningDate: string, accrualPeriodStart?: string): Date {
  // Add T00:00:00 to parse as local time instead of UTC
  const payoutDayOfWeek = getDay(new Date(firstEarningDate + 'T00:00:00'))

  if (
    accrualPeriodStart &&
    accrualPeriodStart !== '1970-01-01' &&
    !isBefore(new Date(firstEarningDate + 'T00:00:00'), new Date(accrualPeriodStart + 'T00:00:00')) &&
    isAfter(addDays(new Date(accrualPeriodStart + 'T00:00:00'), 13), new Date(firstEarningDate + 'T00:00:00'))
  ) {
    const differenceInWeeks = eachWeekOfInterval({
      start: new Date(accrualPeriodStart + 'T00:00:00'),
      end: new Date(firstEarningDate + 'T00:00:00'),
    }).length

    return endOfDay(setDay(addWeeks(new Date(periodStart + 'T00:00:00'), differenceInWeeks - 1), payoutDayOfWeek))
  }
  return endOfDay(nextDay(subDays(new Date(periodEnd + 'T00:00:00'), 1), payoutDayOfWeek))
}

export function calculateProratedQuota(params: { quota: QuotaInUnits; yearStart: Date; userStartDate: Date }): QuotaInUnits {
  const { yearStart, userStartDate, quota } = params
  let yearMultiplier
  if (isAfter(yearStart, userStartDate)) {
    yearMultiplier = 1
  } else {
    const diffInDays = 365 - differenceInDays(userStartDate, yearStart)
    yearMultiplier = diffInDays / 365
  }
  return {
    unit: quota.unit,
    value: Math.round(quota.value * yearMultiplier * 10000) / 10000,
  }
}

export function createZeroQuota(): IQuotaAmount {
  return {
    raw: { days: 0, hours: 0 },
    days: 0,
    hours: 0,
    minutes: 0,
    isNegative: false,
  }
}
